import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Zap, Droplets, Clock, ArrowRight, ShieldCheck, Shield, TrendingDown, Package, Plug, Target, Quote, Gauge } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { ProductCard } from '../components/shared/ProductCard';
import { SEO } from '../components/shared/SEO';
export function HomePage() {
  return <div className="flex flex-col" data-id="element-176">
      <SEO title="Precision Pro Sprayers | ForceX Plug-and-Play Electric Sprayers" description="Professional plug-and-play electric sprayers built for contractors. Eliminate pouring, reduce spills, and finish jobs faster with ForceX sprayer kits." data-id="element-177" />

      {/* Hero Section */}
      <section className="relative bg-brand-dark text-white py-20 lg:py-32 overflow-hidden" data-id="element-178">
        <div className="absolute inset-0 bg-black/75 z-10" data-id="element-179"></div>
        <div className="absolute inset-0 z-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/mnYnLdKB4CX3ZrYpuepiJd/hero3.png)'
      }} data-id="element-180"></div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-181">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight uppercase" data-id="element-182">
            Spray With Precision
          </h1>
          <p className="text-2xl md:text-3xl text-brand-yellow font-bold mb-4 uppercase" data-id="element-183">
            Ditch Your Pump Sprayer
          </p>
          <p className="text-xl md:text-2xl text-gray-200 mb-10 max-w-3xl mx-auto leading-relaxed uppercase font-bold" data-id="element-184">
            Finish Your Job In Half The Time.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-185">
            <Link to="/products/forcex-complete" data-id="element-186">
              <Button variant="primary" size="lg" data-id="element-187">
                View Kits
              </Button>
            </Link>
            <Link to="/contact" data-id="element-188">
              <Button variant="outline" size="lg" className="text-white border-white hover:bg-white hover:text-brand-dark" data-id="element-189">
                Call Now
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Product Preview */}
      <section className="py-20 bg-brand-light" data-id="element-190">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-191">
          <div className="text-center mb-12" data-id="element-192">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-193">
              Professional Grade Kits
            </h2>
            <p className="text-xl text-gray-600" data-id="element-194">
              Choose the system that fits your workflow.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-195">
            <ProductCard title="ForceX – Complete Kit" description="Out of the box, ready to work. Includes enclosed system housing for maximum efficiency and cleanliness." imageLabel="[Product Image - Complete Kit]" linkTo="/products/forcex-complete" price="Best Value" imageUrl="https://cdn.magicpatterns.com/uploads/krkNLYYgMpyS53QTkGfeA8/complete.png" data-id="element-196" />
            <ProductCard title="ForceX – DIY Kit" description="Build it your way. The flexible solution for contractors who want to customize your setup." imageLabel="[Product Image - DIY Kit]" linkTo="/products/forcex-diy" price="Intro Pricing Available" data-id="element-197" />
          </div>
        </div>
      </section>

      {/* Unified Benefits Section */}
      <section className="py-20 bg-white" data-id="element-198">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-199">
          <div className="text-center mb-12" data-id="element-200">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-201">
              Why Contractors Choose Precision Pro Sprayers
            </h2>
            <p className="text-xl text-gray-600" data-id="element-202">
              Designed to eliminate hauling, reduce downtime, and speed up every
              job.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12" data-id="element-203">
            {/* Top Row - Outcome-Focused */}
            <div className="flex items-start space-x-4 p-6 bg-brand-light rounded-lg border border-gray-200" data-id="element-204">
              <div className="flex-shrink-0" data-id="element-205">
                <div className="w-12 h-12 bg-brand-yellow/10 rounded-full flex items-center justify-center" data-id="element-206">
                  <Shield className="h-6 w-6 text-brand-yellow" data-id="element-207" />
                </div>
              </div>
              <div data-id="element-208">
                <h3 className="text-xl font-bold text-brand-dark mb-2" data-id="element-209">
                  Stay Safe
                </h3>
                <p className="text-gray-600" data-id="element-210">
                  Keep chemicals on the ground and reduce exposure in tight
                  spaces.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4 p-6 bg-brand-light rounded-lg border border-gray-200" data-id="element-211">
              <div className="flex-shrink-0" data-id="element-212">
                <div className="w-12 h-12 bg-brand-yellow/10 rounded-full flex items-center justify-center" data-id="element-213">
                  <Clock className="h-6 w-6 text-brand-yellow" data-id="element-214" />
                </div>
              </div>
              <div data-id="element-215">
                <h3 className="text-xl font-bold text-brand-dark mb-2" data-id="element-216">
                  Save Time
                </h3>
                <p className="text-gray-600" data-id="element-217">
                  Faster setup, no pumping, no refilling, no downtime.
                </p>
              </div>
            </div>
            <div className="flex items-start space-x-4 p-6 bg-brand-light rounded-lg border border-gray-200" data-id="element-218">
              <div className="flex-shrink-0" data-id="element-219">
                <div className="w-12 h-12 bg-brand-yellow/10 rounded-full flex items-center justify-center" data-id="element-220">
                  <TrendingDown className="h-6 w-6 text-brand-yellow" data-id="element-221" />
                </div>
              </div>
              <div data-id="element-222">
                <h3 className="text-xl font-bold text-brand-dark mb-2" data-id="element-223">
                  Save Money
                </h3>
                <p className="text-gray-600" data-id="element-224">
                  Fewer spills, less waste, and faster job completion.
                </p>
              </div>
            </div>

            {/* Bottom Row - Feature-Focused */}
            <div className="p-6 rounded-xl bg-brand-light border border-gray-100 text-center" data-id="element-225">
              <div className="w-16 h-16 bg-brand-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-226">
                <Zap className="h-8 w-8 text-brand-yellow" data-id="element-227" />
              </div>
              <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-228">
                Plug-and-Play Electric
              </h3>
              <p className="text-gray-600" data-id="element-229">
                No batteries to charge or fail. Consistent power all day long.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-brand-light border border-gray-100 text-center" data-id="element-230">
              <div className="w-16 h-16 bg-brand-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-231">
                <Droplets className="h-8 w-8 text-brand-yellow" data-id="element-232" />
              </div>
              <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-233">
                75-Foot Hose
              </h3>
              <p className="text-gray-600" data-id="element-234">
                Reach further without moving your setup. Maximum mobility in
                tight spaces.
              </p>
            </div>
            <div className="p-6 rounded-xl bg-brand-light border border-gray-100 text-center" data-id="element-235">
              <div className="w-16 h-16 bg-brand-yellow/10 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-236">
                <Clock className="h-8 w-8 text-brand-yellow" data-id="element-237" />
              </div>
              <h3 className="text-xl font-bold text-brand-dark mb-3" data-id="element-238">
                Faster Jobs
              </h3>
              <p className="text-gray-600" data-id="element-239">
                Less lifting. Less spills. Get in, spray, and get out faster.
              </p>
            </div>
          </div>

          {/* Automatic Pressure Control Section */}
          <div className="bg-brand-dark text-white p-8 rounded-xl border border-gray-700" data-id="element-240">
            <div className="flex flex-col md:flex-row items-center gap-8" data-id="element-241">
              <div className="flex-shrink-0" data-id="element-242">
                <div className="w-20 h-20 bg-brand-yellow/20 rounded-full flex items-center justify-center" data-id="element-243">
                  <Gauge className="h-10 w-10 text-brand-yellow" data-id="element-244" />
                </div>
              </div>
              <div className="text-left" data-id="element-245">
                <h3 className="text-2xl font-bold text-brand-yellow mb-3" data-id="element-246">
                  Automatic Pressure Control (60 PSI)
                </h3>
                <p className="text-gray-300 leading-relaxed max-w-3xl" data-id="element-247">
                  The ForceX system automatically builds pressure up to 60 PSI
                  and then shuts off. When the spray trigger is squeezed,
                  pressure is released and the pump automatically reactivates to
                  restore pressure on demand. This ensures consistent spray
                  performance, prevents over-pressurization, and reduces wear on
                  the system.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 relative overflow-hidden" data-id="element-248">
        {/* Video Background */}
        <div className="absolute inset-0 z-0" data-id="element-249">
          <div className="absolute inset-0 bg-white/40 z-10" data-id="element-250"></div>
          <video autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-251">
            <source src="https://cdn.magicpatterns.com/uploads/mDGvpjFBDgEvF12sEZF8rw/SprayClip.mp4" type="video/mp4" data-id="element-252" />
            <source src="https://cdn.magicpatterns.com/uploads/mDGvpjFBDgEvF12sEZF8rw/SprayClip.mov" type="video/quicktime" data-id="element-253" />
            <source src="https://cdn.magicpatterns.com/uploads/mDGvpjFBDgEvF12sEZF8rw/SprayClip.undefined" type="video/mp4" data-id="element-254" />
          </video>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20" data-id="element-255">
          <div className="text-center mb-16" data-id="element-256">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-257">
              How It Works
            </h2>
            <p className="text-xl text-gray-800 font-medium" data-id="element-258">
              Three simple steps to faster, safer spraying.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-5xl mx-auto" data-id="element-259">
            <div className="text-center" data-id="element-260">
              <div className="w-20 h-20 bg-brand-yellow rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg" data-id="element-261">
                <Package className="h-10 w-10 text-brand-dark" data-id="element-262" />
              </div>
              <div className="text-brand-yellow font-bold text-lg mb-3 drop-shadow-sm" data-id="element-263">
                Step 1
              </div>
              <h3 className="text-2xl font-bold text-brand-dark mb-3" data-id="element-264">
                Order Your Kit
              </h3>
              <p className="text-gray-800 font-medium" data-id="element-265">
                Choose the DIY or Complete ForceX system that fits your
                workflow.
              </p>
            </div>

            <div className="text-center" data-id="element-266">
              <div className="w-20 h-20 bg-brand-yellow rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg" data-id="element-267">
                <Plug className="h-10 w-10 text-brand-dark" data-id="element-268" />
              </div>
              <div className="text-brand-yellow font-bold text-lg mb-3 drop-shadow-sm" data-id="element-269">
                Step 2
              </div>
              <h3 className="text-2xl font-bold text-brand-dark mb-3" data-id="element-270">
                Plug It In
              </h3>
              <p className="text-gray-800 font-medium" data-id="element-271">
                No batteries, no charging. Just reliable, corded power.
              </p>
            </div>

            <div className="text-center" data-id="element-272">
              <div className="w-20 h-20 bg-brand-yellow rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg" data-id="element-273">
                <Target className="h-10 w-10 text-brand-dark" data-id="element-274" />
              </div>
              <div className="text-brand-yellow font-bold text-lg mb-3 drop-shadow-sm" data-id="element-275">
                Step 3
              </div>
              <h3 className="text-2xl font-bold text-brand-dark mb-3" data-id="element-276">
                Spray with Precision
              </h3>
              <p className="text-gray-800 font-medium" data-id="element-277">
                Drop the intake line into your container and get to work.
              </p>
            </div>
          </div>

          <div className="text-center mt-12" data-id="element-278">
            <Link to="/products/forcex-complete" data-id="element-279">
              <Button variant="primary" size="lg" data-id="element-280">
                View Kits
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="py-20 bg-white" data-id="element-281">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-282">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-283">
            <div data-id="element-284">
              <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-8" data-id="element-285">
                Stop Hauling. Start Spraying.
              </h2>
              <div className="space-y-8" data-id="element-286">
                <div className="border-l-4 border-red-500 pl-6" data-id="element-287">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-288">
                    The Old Way
                  </h3>
                  <ul className="space-y-2 text-gray-600" data-id="element-289">
                    <li className="flex items-start" data-id="element-290">
                      <span className="mr-2 text-red-500" data-id="element-291">✕</span> Carrying
                      heavy chemicals into attics & crawlspaces
                    </li>
                    <li className="flex items-start" data-id="element-292">
                      <span className="mr-2 text-red-500" data-id="element-293">✕</span> Pouring into
                      backpacks or small tanks
                    </li>
                    <li className="flex items-start" data-id="element-294">
                      <span className="mr-2 text-red-500" data-id="element-295">✕</span> Battery
                      failures and expensive downtime
                    </li>
                  </ul>
                </div>

                <div className="border-l-4 border-brand-yellow pl-6" data-id="element-296">
                  <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-297">
                    The ForceX Way
                  </h3>
                  <ul className="space-y-2 text-gray-600" data-id="element-298">
                    <li className="flex items-start" data-id="element-299">
                      <CheckCircle2 className="h-5 w-5 text-brand-yellow mr-2 mt-0.5" data-id="element-300" />{' '}
                      External container siphon - leave the heavy lifting behind
                    </li>
                    <li className="flex items-start" data-id="element-301">
                      <CheckCircle2 className="h-5 w-5 text-brand-yellow mr-2 mt-0.5" data-id="element-302" />{' '}
                      Corded reliability - never run out of power
                    </li>
                    <li className="flex items-start" data-id="element-303">
                      <CheckCircle2 className="h-5 w-5 text-brand-yellow mr-2 mt-0.5" data-id="element-304" />{' '}
                      Fast setup, controlled spray, automatic shutoff
                    </li>
                  </ul>
                </div>
              </div>
              <div className="mt-8" data-id="element-305">
                <Link to="/products/forcex-complete" data-id="element-306">
                  <Button variant="primary" data-id="element-307">Upgrade Your Setup</Button>
                </Link>
              </div>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden" data-id="element-308">
              <img src="https://cdn.magicpatterns.com/uploads/o6LcpLcnE3tYBY2tteo4MD/old-way.png" alt="Contractor pouring chemicals into backpack sprayer" className="w-full h-full object-cover" style={{
              objectPosition: '65% center',
              filter: 'brightness(0.88) saturate(0.85)'
            }} data-id="element-309" />
              <div className="absolute inset-0 bg-black/12" data-id="element-310"></div>
              <div className="absolute bottom-4 right-4 text-white/40 text-xs uppercase tracking-wider font-bold" data-id="element-311">
                The Old Way
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-brand-light" data-id="element-312">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-313">
          <div className="text-center mb-16" data-id="element-314">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-315">
              Trusted by Professionals
            </h2>
            <p className="text-xl text-gray-600" data-id="element-316">
              Real feedback from contractors in the field.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-317">
            <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm" data-id="element-318">
              <Quote className="h-10 w-10 text-brand-yellow mb-4" data-id="element-319" />
              <p className="text-gray-700 text-lg mb-6 leading-relaxed" data-id="element-320">
                "Made my attic work faster and safer. No more hauling chemicals
                up ladders."
              </p>
              <div className="flex items-center" data-id="element-321">
                <div className="w-12 h-12 bg-brand-yellow/10 rounded-full flex items-center justify-center mr-4" data-id="element-322">
                  <span className="text-brand-dark font-bold text-lg" data-id="element-323">DC</span>
                </div>
                <div data-id="element-324">
                  <div className="font-bold text-brand-dark" data-id="element-325">Dustin C.</div>
                  <div className="text-gray-500 text-sm" data-id="element-326">Contractor</div>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl border border-gray-200 shadow-sm" data-id="element-327">
              <Quote className="h-10 w-10 text-brand-yellow mb-4" data-id="element-328" />
              <p className="text-gray-700 text-lg mb-6 leading-relaxed" data-id="element-329">
                "This system just works. Plug it in and spray."
              </p>
              <div className="flex items-center" data-id="element-330">
                <div className="w-12 h-12 bg-brand-yellow/10 rounded-full flex items-center justify-center mr-4" data-id="element-331">
                  <ShieldCheck className="h-6 w-6 text-brand-yellow" data-id="element-332" />
                </div>
                <div data-id="element-333">
                  <div className="font-bold text-brand-dark" data-id="element-334">
                    Restoration Contractor
                  </div>
                  <div className="text-gray-500 text-sm" data-id="element-335">Verified Buyer</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 bg-white" data-id="element-336">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-337">
          <div className="text-center mb-12" data-id="element-338">
            <h2 className="text-3xl md:text-4xl font-bold text-brand-dark mb-4" data-id="element-339">
              Who It's For
            </h2>
            <p className="text-xl text-gray-600 mb-8" data-id="element-340">
              Used by professionals in:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto" data-id="element-341">
            {['Mold remediation', 'Disinfecting and sanitation', 'Crawlspaces and attics', 'Facilities maintenance', 'Property management', 'Restoration contractors'].map(industry => <div key={industry} className="flex items-center p-4 bg-brand-light rounded-lg border border-gray-200" data-id="element-342">
                <CheckCircle2 className="h-5 w-5 text-brand-yellow mr-3 flex-shrink-0" data-id="element-343" />
                <span className="text-gray-700 font-medium" data-id="element-344">{industry}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-brand-dark text-white" data-id="element-345">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-346">
          <div className="text-center mb-16" data-id="element-347">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-348">
              Built For Your Industry
            </h2>
            <p className="text-xl text-gray-400" data-id="element-349">
              Trusted by professionals across multiple sectors.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-350">
            {['Mold Remediation', 'Disinfecting & Antimicrobials', 'Facilities Maintenance', 'Contractors', 'Municipal / Commercial', 'Residential Professionals'].map(industry => <div key={industry} className="bg-gray-800 p-6 rounded-lg text-center hover:bg-gray-700 transition-colors border border-gray-700" data-id="element-351">
                <ShieldCheck className="h-8 w-8 text-brand-yellow mx-auto mb-3" data-id="element-352" />
                <h3 className="font-bold text-lg" data-id="element-353">{industry}</h3>
              </div>)}
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-brand-yellow" data-id="element-354">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-355">
          <h2 className="text-3xl md:text-5xl font-bold text-brand-dark mb-6" data-id="element-356">
            Built for Contractors Who Hate Wasted Time
          </h2>
          <p className="text-xl text-gray-800 mb-10 leading-relaxed" data-id="element-357">
            Precision Pro Sprayers are designed to eliminate unnecessary steps,
            reduce exposure, and make spraying faster and safer in tight spaces.
          </p>
          <Link to="/products/forcex-complete" data-id="element-358">
            <Button variant="primary" size="lg" className="bg-brand-dark text-white hover:bg-gray-800" data-id="element-359">
              Shop Sprayers
              <ArrowRight className="ml-2 h-5 w-5" data-id="element-360" />
            </Button>
          </Link>
        </div>
      </section>
    </div>;
}